import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaShieldAlt, FaLeaf, FaArrowRight, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaBrain } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import depressionImage from '../../components/assets/Staff/DEPRESSION1.jpg';

const Depression = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const supportRef = useRef(null);
  const researchRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'support': supportRef,
      'research': researchRef,
      'summary': summaryRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'support', ref: supportRef },
        { id: 'research', ref: researchRef },
        { id: 'summary', ref: summaryRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Holistic Perspective', icon: FaHeart },
    { id: 'support', label: 'Emotional Support', icon: FaLeaf },
    { id: 'research', label: 'Research & Evidence', icon: FaBookOpen },
    { id: 'summary', label: 'In Summary', icon: FaLightbulb }
  ];

  const overviewFeatures = [
    {
      icon: FaBrain,
      title: "Emotional Balance",
      description: "From a holistic wellbeing perspective, emotional challenges are viewed as part of a broader interaction involving the nervous system, stress response and lifestyle factors"
    },
    {
      icon: FaShieldAlt,
      title: "Alongside Care",
      description: "This perspective does not replace medical or psychological models and does not aim to diagnose or explain depression clinically"
    },
    {
      icon: FaLeaf,
      title: "Wellness-Focused",
      description: "Within wellness-focused environments, this frequency-based method is explored as a non-invasive way to support balance and stress regulation"
    }
  ];

  const supportReasons = [
    "feel emotionally overwhelmed or chronically stressed",
    "experience persistent low energy or emotional imbalance",
    "want additional, gentle support alongside medical or psychological care",
    "are looking for a holistic, personalised wellbeing approach"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Depression" description="Bioresonance therapy for emotional balance and depression support at Bioresonance Leicester. Complementary, wellness-focused. Book at 108 New Walk." path="/therapy/bioresonance-therapy/depression" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
          <ol className="flex items-center space-x-2 text-sm text-dark-grey">
            <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
            <li className="text-gold">/</li>
            <li className="text-gold font-semibold">Depression</li>
          </ol>
        </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                DEPRESSION
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed font-semibold">
                Supporting emotional balance and wellbeing in a complementary way
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Low mood, emotional exhaustion and a persistent sense of heaviness can affect every aspect of daily life. For many people, these experiences extend beyond temporary sadness and become long-lasting challenges that influence relationships, work, motivation and overall quality of life.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                While conventional mental health care plays a crucial role, some individuals also explore complementary, non-medical approaches that focus on supporting emotional balance, stress regulation and overall wellbeing alongside standard care.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such option, applied in practice as a gentle, frequency-based approach.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={depressionImage}
                      alt="Depression support"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <button
                  type="button"
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold text-black shadow-lg transform scale-105'
                      : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                  }`}
                  aria-current={activeSection === section.id ? 'location' : undefined}
                >
                  <SectionIcon className="mr-2 text-sm" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {/* Understanding depression from a holistic wellbeing perspective */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding depression from a holistic wellbeing perspective
              </h2>
              <p className="text-lg text-gray-600">
                Depression is commonly described as a condition involving persistent low mood, reduced emotional resilience and loss of interest in everyday activities.
              </p>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              It can affect both mental and physical wellbeing and often coexists with fatigue, sleep disturbances and difficulty concentrating.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                  >
                    <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-2xl text-gold" />
                    </div>
                    <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Instead, it focuses on supporting the individual as a whole, alongside appropriate medical and psychological care.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Within wellness-focused environments, this frequency-based method is explored as a non-invasive way to support balance and stress regulation, alongside other wellbeing practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emotional wellbeing support using frequency-based methods */}
      <section ref={supportRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              Emotional wellbeing support using frequency-based methods
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Sessions using this approach are personalised and adapted to the individual's overall state, stress levels and personal circumstances. The method is non-invasive and does not involve medication or physical manipulation.
            </p>
            
            <p className="text-gray-600 mb-4 leading-relaxed font-semibold">
              Some individuals choose to explore this form of complementary support when they:
            </p>
            <ul className="space-y-3 text-gray-600 leading-relaxed list-none">
              {supportReasons.map((reason, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gold mr-3 mt-1">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Selected research and scientific discussion */}
      <section ref={researchRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Selected research and scientific discussion
              </h2>
              <p className="text-lg text-gray-600">
                This frequency-based approach has been explored in research contexts related to emotional wellbeing, contributing to ongoing scientific discussion.
              </p>
            </div>

            <p className="text-gray-600 mb-8 leading-relaxed">
              One study published in the Journal of Medicine and Life in 2021 examined the use of a bioresonance-based method in individuals diagnosed with recurrent major depressive disorder.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-8">
              <h3 className="text-2xl font-bold text-dark-grey mb-6">Study title:</h3>
              <p className="text-lg text-gray-600 mb-6 font-medium">
                Bioresonance therapy may treat depression
              </p>
              
              <h4 className="text-lg font-semibold text-dark-grey mb-2">Authors:</h4>
              <p className="text-gray-600 mb-6">
                Daniel Mureșan, Andreea Sălcudean, Daniela Claudia Săbau, Cristina Raluca Bodo, Iosif Gaboș Grecu
              </p>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Study overview:</h4>
              <p className="text-gray-600 mb-4">
                <strong>Participants:</strong> 140 individuals diagnosed with recurrent major depressive disorder
              </p>
              
              <h4 className="text-lg font-semibold text-dark-grey mb-2">Study groups:</h4>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>Group 1 – bioresonance only</li>
                <li>Group 2 – antidepressants combined with bioresonance</li>
                <li>Group 3 – antidepressants only</li>
              </ul>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Assessment method:</h4>
              <p className="text-gray-600 mb-6">
                Hamilton Depression Rating Scale over a five-week observation period
              </p>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Reported findings:</h4>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>All groups showed improvement.</li>
                <li>The combined group, using antidepressants alongside a bioresonance-based approach, demonstrated the greatest reduction in depression scores.</li>
                <li>The authors suggested that this frequency-based method may offer supportive value when used alongside conventional care.</li>
              </ul>

              <h4 className="text-lg font-semibold text-dark-grey mb-2">Publication reference:</h4>
              <a 
                href="https://pubmed.ncbi.nlm.nih.gov/34336537/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue hover:text-gold transition-colors duration-200 break-all inline-flex items-center"
              >
                https://pubmed.ncbi.nlm.nih.gov/34336537/
                <FaArrowRight className="ml-2 text-sm flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* In summary */}
      <section ref={summaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              In summary
            </h2>
            
            <div className="space-y-6">
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance, applied as a frequency-based method, has been explored in research related to emotional wellbeing and is discussed as part of a broader, ongoing scientific conversation. It is approached as a non-invasive, personalised and complementary option that some individuals choose to include alongside conventional mental health support.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Within this framework, the approach is often valued for its gentle, holistic focus on emotional balance and individual response. While research continues to evolve, interest remains among those seeking additional, non-invasive ways to support wellbeing as part of an integrative perspective.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Complementary Support for Emotional Wellbeing?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Bioresonance is approached as a non-invasive, personalised and complementary option that some individuals choose to include alongside conventional mental health support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
          <Link 
            to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
          >
              Back to Bioresonance Therapy
          </Link>
        </div>
      </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default Depression;
